import type { AccountResponse, GroupResponse } from "@ccflare/api";
import {
	AccountPresenter,
	formatCostShort,
	formatCountdown,
	formatTokensCompact,
} from "@ccflare/ui";
import {
	AlertCircle,
	CheckCircle,
	Edit2,
	LogIn,
	Pause,
	Play,
	Trash2,
} from "lucide-react";
import type { ReactNode } from "react";
import { ProviderBadge } from "../ProviderBadge";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { CountdownBar } from "./CountdownBar";

function getAuthMethodLabel(authMethod: string): string {
	switch (authMethod) {
		case "oauth":
			return "OAuth";
		case "api_key":
			return "API Key";
		default:
			return authMethod
				.split("_")
				.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
				.join(" ");
	}
}

type BadgeVariant = "success" | "warning" | "destructive" | "secondary";

/**
 * Map Anthropic's unified rate-limit status into a pill. This is the honest
 * "am I close to the limit" signal (the provider's own tier), as opposed to a
 * usage bar — Anthropic publishes no token quota to fill against.
 */
function getLimitPill(rateLimit: AccountResponse["rateLimitStatus"]): {
	variant: BadgeVariant;
	label: string;
} {
	if (rateLimit.isLimited) return { variant: "destructive", label: "Limited" };
	const code = rateLimit.code.toLowerCase();
	if (code.includes("warning") || code.includes("soft"))
		return { variant: "warning", label: "Warning" };
	if (code === "ok" || code === "allowed")
		return { variant: "success", label: "Allowed" };
	return { variant: "secondary", label: rateLimit.code };
}

function ZoneLabel({ children }: { children: ReactNode }) {
	return (
		<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
			{children}
		</p>
	);
}

/**
 * One rolling-window usage row: tokens, plus "Cost" (money actually drawn — €0
 * while in-plan) and "Theoretical" (what those tokens would cost via the API).
 */
function UsageWindow({
	label,
	window,
}: {
	label: string;
	window: { tokens: number; costUsd: number; theoreticalCostUsd: number };
}) {
	return (
		<div className="text-sm">
			<span className="text-muted-foreground">{label}</span>{" "}
			<span className="font-medium">{formatTokensCompact(window.tokens)}</span>
			<span className="text-muted-foreground">
				{" · "}
				<span title="Money actually drawn (€0 while within plan limits)">
					Cost {formatCostShort(window.costUsd)}
				</span>
				{" · "}
				<span title="What these tokens would cost via the Anthropic API">
					Theoretical {formatCostShort(window.theoreticalCostUsd)}
				</span>
			</span>
		</div>
	);
}

/**
 * One provider rate-limit window: "% of limit used" plus the reset countdown.
 * Percent-only by design (no bar) so it stays compact in the limit column.
 */
function LimitWindow({
	label,
	window,
	now,
}: {
	label: string;
	window: { utilization: number | null; resetAt: string | null };
	now: number;
}) {
	if (window.utilization == null) return null;
	const pct = Math.round(window.utilization * 100);
	const resetMs = window.resetAt ? new Date(window.resetAt).getTime() : null;
	return (
		<p className="text-sm">
			<span className="text-muted-foreground">{label}</span>{" "}
			<span className="font-medium">{pct}%</span>
			{resetMs && resetMs > now ? (
				<span className="text-muted-foreground">
					{" · "}resets in {formatCountdown(resetMs - now)}
				</span>
			) : null}
		</p>
	);
}

/** "Status 400: Refresh token expired · since 3h 12m ago (23/10/2026, 09:14)" */
function describeAuthError(account: AccountResponse, now: number): string {
	const sinceMs = account.authErrorAt
		? new Date(account.authErrorAt).getTime()
		: null;
	const since = sinceMs
		? ` · since ${formatCountdown(Math.max(0, now - sinceMs))} ago (${new Date(sinceMs).toLocaleString()})`
		: "";
	return `${account.authError ?? "Login expired"}${since}`;
}

interface AccountListItemProps {
	account: AccountResponse;
	isActive?: boolean;
	onPauseToggle: (account: AccountResponse) => void;
	onRemove: (account: AccountResponse) => void;
	onRename: (account: AccountResponse) => void;
	/** Start an OAuth re-login for this account (OAuth accounts only). */
	onReauth?: (account: AccountResponse) => void;
	groups?: GroupResponse[];
	onMoveGroup?: (account: AccountResponse, groupId: string) => void;
}

export function AccountListItem({
	account,
	isActive = false,
	onPauseToggle,
	onRemove,
	onRename,
	onReauth,
	groups,
	onMoveGroup,
}: AccountListItemProps) {
	const presenter = new AccountPresenter(account);
	const { rotation, usage, rateLimitStatus, rateLimitReset, rateLimitWindows } =
		account;
	const now = Date.now();
	const hasWindowData =
		rateLimitWindows.fiveHour.utilization != null ||
		rateLimitWindows.sevenDay.utilization != null;

	const limitPill = getLimitPill(rateLimitStatus);
	const benchedUntilMs = rateLimitStatus.until
		? new Date(rateLimitStatus.until).getTime()
		: null;
	const resetMs = rateLimitReset ? new Date(rateLimitReset).getTime() : null;
	// A dead login takes the account out of routing; its rate-limit and
	// rotation data are stale, so they must not read as "Allowed"/"Active".
	const needsReauth = presenter.needsReauth;
	const authErrorText = needsReauth ? describeAuthError(account, now) : "";
	const canReauth = account.auth_method === "oauth" && !!onReauth;

	return (
		<div
			className={`p-4 border rounded-lg transition-colors space-y-4 ${
				needsReauth
					? "border-destructive bg-destructive/5"
					: isActive
						? "border-primary bg-primary/5 shadow-sm"
						: "border-border hover:border-muted-foreground/50"
			}`}
		>
			{/* Header: identity, badges, group, actions */}
			<div className="flex items-start justify-between gap-2">
				<div>
					<div className="flex items-center gap-2">
						<p className="font-medium">{account.name}</p>
						{needsReauth ? (
							<span title={authErrorText}>
								<Badge variant="destructive">Re-login required</Badge>
							</span>
						) : (
							isActive && (
								<span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
									Active
								</span>
							)
						)}
						{account.paused && (
							<span className="text-xs text-muted-foreground">Paused</span>
						)}
					</div>
					<div className="mt-1 flex flex-wrap items-center gap-2">
						<ProviderBadge provider={account.provider} />
						<Badge
							variant={
								account.auth_method === "oauth" ? "secondary" : "outline"
							}
						>
							{getAuthMethodLabel(account.auth_method)}
						</Badge>
						<span className="text-sm text-muted-foreground">
							{presenter.weightDisplay}
						</span>
						<span className="text-sm text-muted-foreground">
							{presenter.requestCount} reqs
						</span>
						{groups && groups.length > 0 && onMoveGroup && (
							<Select
								value={account.groupId}
								onValueChange={(newId) => onMoveGroup(account, newId)}
							>
								<SelectTrigger className="h-6 w-auto min-w-[90px] text-xs px-2 border-dashed">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{groups.map((g) => (
										<SelectItem key={g.id} value={g.id} className="text-xs">
											{g.name}
											{g.isDefault ? " (default)" : ""}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2 shrink-0">
					{canReauth && (
						<Button
							variant={needsReauth ? "destructive" : "ghost"}
							size="sm"
							onClick={() => onReauth?.(account)}
							title="Log in again with OAuth (keeps name, group and stats)"
						>
							<LogIn className="h-4 w-4" />
							{needsReauth && <span className="ml-1">Re-login</span>}
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onRename(account)}
						title="Rename account"
					>
						<Edit2 className="h-4 w-4" />
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onPauseToggle(account)}
						title={account.paused ? "Resume account" : "Pause account"}
					>
						{account.paused ? (
							<Play className="h-4 w-4" />
						) : (
							<Pause className="h-4 w-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onRemove(account)}
						title="Delete account"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Three zones: rotation (ccflare) · provider limit · usage */}
			<div className="grid grid-cols-1 gap-4 border-t border-border pt-3 sm:grid-cols-3">
				{/* Zone 1: Rotation (ccflare's own scheduler) */}
				<div className="space-y-2">
					<ZoneLabel>Rotation · ccflare</ZoneLabel>
					{needsReauth ? (
						<p className="text-sm text-destructive" title={authErrorText}>
							Excluded from rotation · login expired
						</p>
					) : account.paused ? (
						<p className="text-sm text-muted-foreground">Paused</p>
					) : rotation.isActive && rotation.live ? (
						<>
							<p className="text-sm">
								<span className="font-medium text-success">● Active</span>
								{account.sessionInfo.requestCount > 0 && (
									<span className="text-muted-foreground">
										{" · "}
										{account.sessionInfo.requestCount} reqs this session
									</span>
								)}
							</p>
							{rotation.nextSwitchAt && rotation.windowMinutes ? (
								<CountdownBar
									label="Account turn"
									verb="switches in"
									endIso={rotation.nextSwitchAt}
									windowMs={rotation.windowMinutes * 60000}
								/>
							) : null}
						</>
					) : rotation.isActive ? (
						// Group's current account, but its rotation window has lapsed
						// (idle between bursts). The next request may start a fresh turn.
						<p className="text-sm">
							<span className="font-medium text-success">● Active</span>
							<span className="text-muted-foreground">
								{" · "}idle
								{account.lastUsed
									? ` · last used ${formatCountdown(now - new Date(account.lastUsed).getTime())} ago`
									: ""}
							</span>
						</p>
					) : (
						<p className="text-sm text-muted-foreground">
							○ Standby
							{rotation.windowMinutes
								? ` · rotates every ${formatCountdown(rotation.windowMinutes * 60000)}`
								: ""}
						</p>
					)}
				</div>

				{/* Zone 2: provider (Anthropic) limit */}
				<div className="space-y-2">
					<ZoneLabel>{account.provider} limit</ZoneLabel>
					{needsReauth ? (
						<>
							<div className="flex items-center gap-2" title={authErrorText}>
								<AlertCircle className="h-4 w-4 text-destructive" />
								<Badge variant="destructive">Login expired</Badge>
							</div>
							<p className="text-sm text-destructive break-words">
								{authErrorText}
							</p>
							<p className="text-sm text-muted-foreground">
								Limit status unknown until re-login
								{hasWindowData ? " · last known:" : ""}
							</p>
							{hasWindowData && (
								<div className="space-y-0.5 opacity-60">
									<LimitWindow
										label="5h"
										window={rateLimitWindows.fiveHour}
										now={now}
									/>
									<LimitWindow
										label="7d"
										window={rateLimitWindows.sevenDay}
										now={now}
									/>
								</div>
							)}
						</>
					) : (
						<>
							<div className="flex items-center gap-2">
								{rateLimitStatus.isLimited ? (
									<AlertCircle className="h-4 w-4 text-destructive" />
								) : (
									<CheckCircle className="h-4 w-4 text-success" />
								)}
								<Badge variant={limitPill.variant}>{limitPill.label}</Badge>
							</div>
							{rateLimitStatus.isLimited && benchedUntilMs ? (
								<p className="text-sm text-destructive">
									limited for {formatCountdown(benchedUntilMs - now)}
								</p>
							) : hasWindowData ? (
								// Provider-reported rolling windows: "% of limit used" for the
								// 5-hour and weekly buckets, each with its own reset countdown.
								<div className="space-y-0.5">
									<LimitWindow
										label="5h"
										window={rateLimitWindows.fiveHour}
										now={now}
									/>
									<LimitWindow
										label="7d"
										window={rateLimitWindows.sevenDay}
										now={now}
									/>
								</div>
							) : resetMs && resetMs > now ? (
								<p className="text-sm text-muted-foreground">
									resets in {formatCountdown(resetMs - now)}
									<span className="text-muted-foreground/70">
										{" "}
										({new Date(resetMs).toLocaleTimeString()})
									</span>
								</p>
							) : (
								// A reset time in the past means the last observed window has
								// already elapsed; for an idle/standby account no fresh request
								// has updated it, so there is no active limit window to count down.
								<p className="text-sm text-muted-foreground">
									no limit reported
								</p>
							)}
						</>
					)}
				</div>

				{/* Zone 3: usage from our own request log (rolling windows) */}
				<div className="space-y-2">
					<ZoneLabel>Usage (this account)</ZoneLabel>
					<UsageWindow label="last 5h" window={usage.fiveHour} />
					<UsageWindow label="last 7d" window={usage.sevenDay} />
				</div>
			</div>
		</div>
	);
}
